import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from './AppProvider';
import { DataError } from './source';

export const keys = {
  me: ['me'] as const,
  trips: ['trips'] as const,
  trip: (id: string) => ['trip', id] as const,
  bookings: (id: string) => ['bookings', id] as const,
  attention: (id: string) => ['attention', id] as const,
  checklist: (id: string) => ['checklist', id] as const,
  groups: ['groups'] as const,
  group: (id: string) => ['group', id] as const,
  documents: ['documents'] as const,
  budget: (id: string) => ['budget', id] as const,
  travelProfile: ['travel-profile'] as const,
  destinations: ['destinations'] as const,
  saved: ['saved-destinations'] as const,
  conversations: ['conversations'] as const,
  conversation: (id: string) => ['conversation', id] as const,
  memory: ['travel-memory'] as const,
};

export function useMe() {
  const { data, session } = useApp();
  return useQuery({ queryKey: keys.me, queryFn: () => data.getMe(), enabled: Boolean(session) });
}
export function useTrips() {
  const { data, session } = useApp();
  return useQuery({ queryKey: keys.trips, queryFn: () => data.listTrips(), enabled: Boolean(session) });
}
export function useTrip(id: string | null | undefined) {
  const { data, session } = useApp();
  return useQuery({ queryKey: keys.trip(id ?? ''), queryFn: () => data.getTrip(id!), enabled: Boolean(session && id) });
}
export function useGroups() {
  const { data, session } = useApp();
  return useQuery({ queryKey: keys.groups, queryFn: () => data.listGroups(), enabled: Boolean(session) });
}
export function useAttention(tripId: string | null | undefined) {
  const { data, session } = useApp();
  return useQuery({ queryKey: keys.attention(tripId ?? ''), queryFn: () => data.listAttentionActions(tripId!), enabled: Boolean(session && tripId) });
}

/** Mutation helper: runs, invalidates the given keys, announces success or a friendly error. */
export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R>, opts: { invalidate?: readonly (readonly unknown[])[]; success?: string } = {}) {
  const qc = useQueryClient();
  const { announce } = useApp();
  return useMutation({
    mutationFn: (args: A) => fn(...args),
    onSuccess: async () => {
      await Promise.all((opts.invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k as unknown[] })));
      if (opts.success) announce(opts.success);
    },
    onError: async (e) => {
      if (e instanceof DataError && e.code === 'VERSION_CONFLICT') await Promise.all((opts.invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k as unknown[] })));
      announce(errorMessage(e));
    },
  });
}

export function errorMessage(e: unknown): string {
  if (e instanceof DataError) return e.message;
  return 'Something went wrong. Please try again.';
}
